import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import {
  ApiResponse,
  EditOrganisationRequest,
  Organisation,
} from "../../types/organisation";

export const organisationApi = createApi({
  reducerPath: "organisationApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Organisation", "Organisations"],

  endpoints: (builder) => ({
    // Get all organisations
    getOrganisations: builder.query<Organisation[], void>({
      query: () => ({
        url: `${ApiEndpoints.organisation}`,
        method: "GET",
      }),
      transformResponse: (res: any) => {
        const list = Array.isArray(res?.results) ? res.results : [];
        return list.map((org: any) => ({
          orgid: String(org?._id ?? org?.id ?? ""),
          _id: String(org?._id ?? org?.id ?? ""),
          name: org?.name ?? "",
          email: org?.email ?? "",
          phone: String(org?.phone ?? ""),
          address: org?.address ?? org?.billing ?? "",
          gyms: org?.gyms || [],
          city: org?.city ?? "",
          state: org?.state ?? "",
          country: org?.country ?? "",
          currency  : org?.currency ?? "",
          postal: org?.postal ?? org?.postal ?? "",
          status: org?.status ?? "Active",
        }));
      },
      providesTags: ["Organisations"],
    }),

    // Get organisation by ID
    getOrganisationById: builder.query<Organisation, string>({
      query: (orgid) => ({
        url: `${ApiEndpoints.organisation}/${orgid}`,
        method: "GET",
      }),
      transformResponse: (res: any): Organisation => {
        const org = res?.result ?? res?.results ?? res ?? {};
        return {
          orgid: String(org?._id ?? org?.id ?? ""),
          _id: String(org?._id ?? org?.id ?? ""),
          name: org?.name ?? "",
          email: org?.email ?? "",
          phone: String(org?.phone ?? ""),
          address: org?.address ?? org?.billing ?? "",
          city: org?.city ?? "",
          state: org?.state ?? "",
          country: org?.country ?? "",
          postal: org?.postal ?? org?.postal ?? "",
          status: org?.status ?? "Active",
        };
      },
      providesTags: (_result, _error, orgid) => [
        { type: "Organisation", id: orgid },
      ],
    }),

    // Create organisation
    createOrganisation: builder.mutation<
      ApiResponse<Organisation>,
      EditOrganisationRequest
    >({
      query: (body) => ({
        url: `${ApiEndpoints.organisation}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Organisations"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Organisation created successfully";
          toast.success(msg);
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to create organisation";
          toast.error(String(msg));
        }
      },
    }),

    // Edit organisation
    editOrganisation: builder.mutation<
      ApiResponse<Organisation>,
      { orgid: string; data: EditOrganisationRequest }
    >({
      query: ({ orgid, data }) => ({
        url: `${ApiEndpoints.editOrganisation}/${orgid}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "Organisation", id: orgid },
        "Organisations",
      ],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Organisation updated successfully";
          toast.success(msg);
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to update organisation";
          toast.error(String(msg));
        }
      },
    }),

    // Delete organisation with optimistic update
    deleteOrganisation: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (orgid) => ({
        url: `${ApiEndpoints.deleteOrganisation}/${orgid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Organisations"],
      async onQueryStarted(orgid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisations",
            undefined,
            (draft: Draft<Organisation[]>) => {
              return draft.filter((org) => org.orgid !== orgid);
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Organisation deleted successfully";
          toast.success(msg);
        } catch (err: any) {
          patchResult.undo();
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to delete organisation";
          toast.error(String(msg));
        }
      },
    }),

    // Toggle organisation status
    toggleOrganisationStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { orgid: string; status: "Active" | "Inactive" }
    >({
      query: ({ orgid, status }) => ({
        url: `${ApiEndpoints.organisationStatusChange}/${orgid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "Organisation", id: orgid },
        "Organisations",
      ],
      async onQueryStarted({ orgid, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisations",
            undefined,
            (draft: Draft<Organisation[]>) => {
              const org = draft.find((o) => o.orgid === orgid);
              if (org) org.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          organisationApi.util.updateQueryData(
            "getOrganisationById",
            orgid,
            (draft: Draft<Organisation>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            `Organisation ${
              status === "Active" ? "activated" : "deactivated"
            } successfully`;
          toast.success(msg);
        } catch (err: any) {
          patchList.undo();
          patchDetail.undo();
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to update organisation status";
          toast.error(String(msg));
        }
      },
    }),
  }),
});

export const {
  useGetOrganisationsQuery,
  useGetOrganisationByIdQuery,
  useCreateOrganisationMutation,
  useEditOrganisationMutation,
  useDeleteOrganisationMutation,
  useToggleOrganisationStatusMutation,
} = organisationApi;
